import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Form, Input, Modal, Popconfirm, Select, Skeleton, Table, Tag, Tooltip, message } from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  EditOutlined,
  FilterOutlined,
  MailOutlined,
  SendOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import DataTable from '@/components/data-table';
import { ClearAllFiltersButton, ToggleFilterButton } from '@/components/data-table-actions';
import { HowToCard } from '@/components/how-to-card';
import { Sidebar } from '@/components/sidebar';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import {
  createContactAccount,
  getCampaignTemplate,
  getContactCampaign,
  previewCampaign,
  remindCampaign,
  saveCampaignTemplate,
  sendCampaign,
  sendContactInvitations,
  testCampaign,
} from '@/services/teamAssignments.service';
import { useAuthStore } from '@/store/useAuthStore';

const STATES = ['NOT_SENT', 'NO_ACCOUNT', 'NO_CONTACT', 'FAILED', 'SENT', 'CONFIRMED'];
const STATE_COLOR = { NOT_SENT: 'blue', NO_ACCOUNT: 'orange', NO_CONTACT: 'red', FAILED: 'red', SENT: 'gold', CONFIRMED: 'green' };
const CAN_ANSWER = ['CLIENT_USER', 'LINKED'];
const HOW_TO_ICONS = [EditOutlined, TeamOutlined, MailOutlined, SendOutlined, BellOutlined];
const FILTER_DEFAULTS = Object.freeze({ campaignSearch: undefined, campaignState: undefined });

const errorText = (e) => e?.response?.data?.message || e.message;
const str = (v) => (Array.isArray(v) ? v.join(',') : v === undefined || v === null ? '' : String(v));
const day = (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '');

/** The text of the mail, in Serbian and in English. */
function TemplateDialog({ open, onClose }) {
  const { t } = useTranslation('teamContacts');
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['contact-campaign-template'], queryFn: getCampaignTemplate, enabled: open });
  useEffect(() => {
    if (open && data) form.setFieldsValue(data.template);
  }, [open, data, form]);
  const save = useMutation({
    mutationFn: saveCampaignTemplate,
    onSuccess: () => {
      message.success(t('review.saved'));
      queryClient.invalidateQueries({ queryKey: ['contact-campaign-template'] });
      onClose();
    },
    onError: (e) => message.error(errorText(e)),
  });
  return (
    <Modal
      open={open}
      width={860}
      title={t('campaign.templateTitle')}
      onCancel={onClose}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button shape="round" onClick={() => form.setFieldsValue(data?.defaults)}>
            {t('campaign.templateDefault')}
          </Button>
          <Button shape="round" onClick={onClose}>
            {t('actions.cancel', { ns: 'teamAssignments' })}
          </Button>
          <Button type="primary" shape="round" loading={save.isPending} onClick={() => form.submit()}>
            {t('actions.save', { ns: 'teamAssignments' })}
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <Skeleton active />
      ) : (
        <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          <p className="mt-0 text-sm opacity-70">{t('campaign.templateHint')}</p>
          <Form.Item name="subjectSr" label={t('campaign.subjectSr')} rules={[{ required: true }]}>
            <Input maxLength={300} />
          </Form.Item>
          <Form.Item name="bodySr" label={t('campaign.bodySr')} rules={[{ required: true }]}>
            <Input.TextArea rows={8} maxLength={8000} />
          </Form.Item>
          <Form.Item name="subjectEn" label={t('campaign.subjectEn')} rules={[{ required: true }]}>
            <Input maxLength={300} />
          </Form.Item>
          <Form.Item name="bodyEn" label={t('campaign.bodyEn')} rules={[{ required: true }]}>
            <Input.TextArea rows={8} maxLength={8000} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

/** Who a mail would go to, how it looks, a test to ourselves, then the send itself. */
function SendDialog({ open, companyIds, reminder, onClose, onSent }) {
  const { t } = useTranslation('teamContacts');
  const ownEmail = useAuthStore((state) => state.user?.email);
  const [testTo, setTestTo] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [previewOf, setPreviewOf] = useState(null);

  useEffect(() => {
    if (open) {
      setAgreed(false);
      setPreviewOf(null);
      setTestTo(ownEmail || '');
    }
  }, [open, ownEmail]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['contact-campaign-preview', companyIds, reminder, previewOf],
    queryFn: () => previewCampaign({ companyIds, reminder, previewCompanyId: previewOf }),
    enabled: open && companyIds.length > 0,
    retry: false,
    gcTime: 0,
  });
  const send = useMutation({
    mutationFn: () =>
      (reminder ? remindCampaign : sendCampaign)({
        companyIds,
        confirmed: true,
        confirmRecipients: data.totals.recipients,
        confirmCompanies: data.totals.companies,
      }),
    onSuccess: (result) => {
      message.success(t('campaign.sent', { count: result.queued }));
      onSent();
    },
    onError: (e) => {
      message.error(errorText(e));
      setAgreed(false);
      refetch();
    },
  });
  const test = useMutation({
    mutationFn: () => testCampaign({ companyIds: [data.preview.companyId], to: testTo, reminder }),
    onSuccess: (r) => message.success(t('campaign.testSent', { to: r.to })),
    onError: (e) => message.error(errorText(e)),
  });

  const totals = data?.totals;
  return (
    <Modal
      open={open}
      width={1100}
      title={reminder ? t('campaign.reminderTitle') : t('campaign.sendTitle')}
      onCancel={onClose}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button shape="round" onClick={onClose}>
            {t('actions.cancel', { ns: 'teamAssignments' })}
          </Button>
          <Button
            type="primary"
            shape="round"
            icon={<SendOutlined />}
            disabled={!agreed || !totals?.companies}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            {reminder ? t('campaign.sendReminder') : t('campaign.send')}
          </Button>
        </div>
      }
    >
      {isLoading && <Skeleton active />}
      {error && <Alert type="error" showIcon message={errorText(error)} />}
      {data && (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
          <Alert
            type={totals.companies ? 'info' : 'warning'}
            showIcon
            message={t('campaign.summary', { companies: totals.companies, recipients: totals.recipients })}
            description={
              data.skipped.length > 0
                ? t('campaign.skipped', {
                    list: data.skipped.map((s) => `${s.name} (${t(`campaign.reason.${s.reason}`)})`).join(', '),
                  })
                : null
            }
          />
          {data.mails.length > 0 && (
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
              <div className="max-h-[440px] overflow-y-auto">
                <Table
                  size="small"
                  rowKey="_id"
                  pagination={false}
                  tableLayout="fixed"
                  dataSource={data.mails}
                  rowClassName={(r) => (r._id === data.preview?.companyId ? 'bg-blue-50' : 'cursor-pointer')}
                  onRow={(r) => ({ onClick: () => setPreviewOf(r._id) })}
                  columns={[
                    { title: t('columns.company'), dataIndex: 'name', width: '34%', render: (v) => <div className="whitespace-normal font-medium">{v}</div> },
                    {
                      title: t('campaign.recipients'),
                      key: 'list',
                      render: (_, r) => (
                        <div className="whitespace-normal text-xs [overflow-wrap:anywhere]">
                          <div>{r.to.join(', ')}</div>
                          {r.cc.length > 0 && <div className="opacity-60">{t('campaign.copyList', { list: r.cc.join(', ') })}</div>}
                        </div>
                      ),
                    },
                    { title: t('campaign.language'), dataIndex: 'language', width: 90, render: (v) => t(`campaign.lang.${v}`) },
                  ]}
                />
              </div>
              <div className="grid grid-rows-[auto_minmax(0,1fr)] gap-2">
                <div className="text-sm font-medium [overflow-wrap:anywhere]">{data.preview?.subject}</div>
                <iframe
                  title={t('campaign.previewTitle')}
                  sandbox=""
                  srcDoc={data.preview?.html}
                  className="h-[410px] w-full rounded-lg border border-solid border-black/10 bg-white"
                />
              </div>
            </div>
          )}
          {data.mails.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm">{t('campaign.testLabel')}</span>
              <Input className="max-w-[320px]" size="small" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
              <Button size="small" shape="round" icon={<MailOutlined />} loading={test.isPending} onClick={() => test.mutate()}>
                {t('campaign.testSend')}
              </Button>
            </div>
          )}
          {totals.companies > 0 && (
            <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
              <b>{t('campaign.agree', { companies: totals.companies, recipients: totals.recipients })}</b>
            </Checkbox>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Who among a company's contacts can sign in; accounts and invitations, by hand. */
function AccountsDialog({ row, onClose, onChanged }) {
  const { t } = useTranslation('teamContacts');
  const create = useMutation({
    mutationFn: (contactId) => createContactAccount({ companyId: row._id, contactId }),
    onSuccess: () => {
      message.success(t('campaign.accountCreated'));
      onChanged();
    },
    onError: (e) => message.error(errorText(e)),
  });
  const invite = useMutation({
    mutationFn: (contactId) => sendContactInvitations({ companyId: row._id, contactIds: [contactId], confirmed: true }),
    onSuccess: (r) => {
      message.success(t('campaign.invitationSent', { to: r.results?.[0]?.email }));
      onChanged();
    },
    onError: (e) => message.error(errorText(e)),
  });
  return (
    <Modal
      open={!!row}
      width={860}
      title={`${t('campaign.accountsTitle')}${row ? `: ${row.name}` : ''}`}
      onCancel={onClose}
      destroyOnClose
      footer={null}
    >
      <p className="mt-0 text-sm opacity-70">{t('campaign.accountsHint')}</p>
      <Table
        size="small"
        rowKey="contactId"
        pagination={false}
        tableLayout="fixed"
        dataSource={row?.accounts || []}
        columns={[
          {
            title: t('campaign.contact'),
            key: 'contact',
            width: '38%',
            render: (_, a) => (
              <div className="whitespace-normal [overflow-wrap:anywhere]">
                {a.name ? <div>{a.name}</div> : null}
                <div className="text-xs opacity-70">{a.email}</div>
              </div>
            ),
          },
          {
            title: t('campaign.account'),
            key: 'state',
            render: (_, a) => (
              <div className="whitespace-normal">
                <Tag color={CAN_ANSWER.includes(a.state) ? 'green' : 'orange'} className="m-0">
                  {t(`campaign.accountState.${a.state}`)}
                </Tag>
                {CAN_ANSWER.includes(a.state) && !a.activated && (
                  <div className="mt-1 text-xs opacity-70">{t('campaign.notActivated')}</div>
                )}
              </div>
            ),
          },
          {
            title: '',
            key: 'action',
            width: 190,
            render: (_, a) => {
              if (['NONE', 'NOT_LINKED'].includes(a.state)) {
                return (
                  <Popconfirm
                    title={t(a.state === 'NONE' ? 'campaign.createConfirm' : 'campaign.linkConfirm', { email: a.email })}
                    okButtonProps={{ shape: 'round' }}
                    cancelButtonProps={{ shape: 'round' }}
                    okText={t('campaign.yes')}
                    cancelText={t('actions.cancel', { ns: 'teamAssignments' })}
                    onConfirm={() => create.mutate(a.contactId)}
                  >
                    <Button size="small" shape="round" icon={<UserAddOutlined />} loading={create.isPending}>
                      {t(a.state === 'NONE' ? 'campaign.createAccount' : 'campaign.linkAccount')}
                    </Button>
                  </Popconfirm>
                );
              }
              if (CAN_ANSWER.includes(a.state) && !a.activated) {
                return (
                  <Popconfirm
                    title={t('campaign.inviteConfirm', { email: a.email })}
                    okButtonProps={{ shape: 'round' }}
                    cancelButtonProps={{ shape: 'round' }}
                    okText={t('campaign.yes')}
                    cancelText={t('actions.cancel', { ns: 'teamAssignments' })}
                    onConfirm={() => invite.mutate(a.contactId)}
                  >
                    <Button size="small" shape="round" icon={<MailOutlined />} loading={invite.isPending}>
                      {t('campaign.invite')}
                    </Button>
                  </Popconfirm>
                );
              }
              return null;
            },
          },
        ]}
      />
    </Modal>
  );
}

/**
 * The mail to clients: choose the companies, look at what would go out, test it
 * on ourselves, and send it knowing how many people it reaches. Nothing leaves
 * without someone here pressing the button, and a company is mailed once.
 */
export default function ContactCampaignView() {
  const { t } = useTranslation(['teamContacts', 'tables', 'forms']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(null);
  const [accountsOf, setAccountsOf] = useState(null);

  const filters = useMemo(
    () => ({
      search: str(queryParams.campaignSearch) || undefined,
      state: str(queryParams.campaignState) || undefined,
    }),
    [queryParams]
  );
  const offset = str(queryParams.offset) || '1';
  const limit = str(queryParams.limit) || '50';
  const { data, status, error } = useQuery({
    queryKey: ['contact-campaign', filters, offset, limit],
    queryFn: () => getContactCampaign({ ...filters, offset, limit }),
    placeholderData: keepPreviousData,
    retry: false,
  });

  useEffect(() => {
    form.setFieldsValue({ search: filters.search || '', state: filters.state });
  }, [filters, form]);

  // The row in the dialog follows the list as accounts are made.
  const accountsRow = accountsOf ? data?.items?.find((r) => r._id === accountsOf) : null;

  const reload = () => queryClient.invalidateQueries({ queryKey: ['contact-campaign'] });
  const applyFilters = (values) => {
    setQueryParams({ campaignSearch: values.search?.trim() || undefined, campaignState: values.state || undefined, offset: 1 });
    setFiltersOpen(false);
  };
  const resetFilters = () => {
    setQueryParams({ ...FILTER_DEFAULTS, offset: 1 });
    form.resetFields();
  };

  const totals = data?.totals || {};
  const columns = [
    {
      title: t('columns.company'),
      dataIndex: 'name',
      width: '22%',
      render: (name, row) => (
        <Tooltip title={row.legalName !== name ? row.legalName : null} placement="topLeft">
          <div className="whitespace-normal font-semibold [overflow-wrap:anywhere]">{name}</div>
        </Tooltip>
      ),
    },
    {
      title: t('campaign.status'),
      dataIndex: 'state',
      width: 130,
      render: (state, row) => (
        <Tooltip title={row.error || null}>
          <Tag color={STATE_COLOR[state]} className="m-0">
            {t(`campaign.state.${state}`)}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: t('campaign.recipients'),
      key: 'to',
      render: (_, row) =>
        row.to.length ? (
          <div className="whitespace-normal text-[13px] leading-5 [overflow-wrap:anywhere]">
            {row.to.join(', ')}
            {row.cc.length > 0 && <div className="text-xs opacity-60">{t('campaign.copyList', { list: row.cc.join(', ') })}</div>}
          </div>
        ) : (
          <span className="opacity-40">—</span>
        ),
    },
    {
      title: t('campaign.language'),
      dataIndex: 'language',
      width: 90,
      render: (v) => t(`campaign.lang.${v}`),
    },
    {
      title: t('campaign.sentAt'),
      key: 'sentAt',
      width: 120,
      render: (_, row) => (
        <div className="whitespace-normal text-xs">
          {day(row.sentAt) || '—'}
          {row.reminderAt && <div className="opacity-60">{t('campaign.reminderAt', { date: day(row.reminderAt) })}</div>}
        </div>
      ),
    },
    {
      title: t('campaign.accounts'),
      key: 'accounts',
      width: 130,
      render: (_, row) =>
        row.accounts.length ? (
          <Button size="small" shape="round" icon={<TeamOutlined />} onClick={() => setAccountsOf(row._id)}>
            {row.accounts.filter((a) => CAN_ANSWER.includes(a.state)).length}/{row.accounts.length}
          </Button>
        ) : null,
    },
  ];

  const howToSteps = t('campaign.howTo.steps', { returnObjects: true });

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
      <HowToCard
        title={t('howTo.title')}
        sections={[
          {
            title: t('campaign.tab'),
            steps: (Array.isArray(howToSteps) ? howToSteps : []).map((step, i) => ({ ...step, icon: HOW_TO_ICONS[i] || FilterOutlined })),
          },
        ]}
      />
      <div className="flex flex-wrap gap-2">
        {STATES.map((s) => (
          <Tag key={s} color={STATE_COLOR[s]} className="m-0 cursor-pointer" onClick={() => setQueryParams({ campaignState: s, offset: 1 })}>
            {t(`campaign.state.${s}`)}: {totals[s] ?? 0}
          </Tag>
        ))}
      </div>
      <DataTable
        rowKey="_id"
        title={`${t('campaign.title')}${data ? ` · ${data.count}` : ''}`}
        data={data?.items || []}
        total={data?.count || 0}
        status={status === 'pending' ? 'pending' : error ? 'error' : 'success'}
        columns={columns}
        offset={offset}
        limit={limit}
        onChangePagination={() => {}}
        tableLayout="fixed"
        size="small"
        rowSelection={{
          selectedRowKeys: selected,
          preserveSelectedRowKeys: true,
          onChange: (keys) => setSelected(keys),
          columnWidth: 40,
        }}
        locale={error ? { emptyText: errorText(error) } : undefined}
        headerChildren={
          <>
            <Button className="btn-default h-8 text-black" shape="round" icon={<EditOutlined />} onClick={() => setTemplateOpen(true)}>
              {t('campaign.template')}
            </Button>
            <Button
              className="btn-default h-8 text-black"
              shape="round"
              icon={<SendOutlined />}
              disabled={!selected.length}
              onClick={() => setSending({ reminder: false })}
            >
              {t('campaign.openSend', { count: selected.length })}
            </Button>
            <Button
              className="btn-default h-8 text-black"
              shape="round"
              icon={<BellOutlined />}
              disabled={!selected.length}
              onClick={() => setSending({ reminder: true })}
            >
              {t('campaign.openReminder')}
            </Button>
            <ToggleFilterButton label={t('filters', { ns: 'tables' })} onClick={() => setFiltersOpen(true)} defaultParams={FILTER_DEFAULTS} />
            <ClearAllFiltersButton label={t('clearAllFilters', { ns: 'tables' })} onReset={resetFilters} defaultParams={FILTER_DEFAULTS} />
          </>
        }
      />
      <Sidebar title={t('filters', { ns: 'tables' })} open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <Form form={form} layout="vertical" className="form p-4" onFinish={applyFilters}>
          <Form.Item name="search" label={t('filter.search')}>
            <Input allowClear placeholder={t('filter.searchPlaceholder')} />
          </Form.Item>
          <Form.Item name="state" label={t('campaign.status')}>
            <Select allowClear placeholder={t('filter.any')} options={STATES.map((s) => ({ value: s, label: t(`campaign.state.${s}`) }))} />
          </Form.Item>
          <div className="form-footer form-footer--sticky">
            <div className="flex gap-2">
              <Button type="default" className="btn-default btn-default--hover" onClick={resetFilters}>
                {t('reset', { ns: 'forms' })}
              </Button>
              <Button type="primary" htmlType="submit" className="btn-primary">
                {t('submit', { ns: 'forms' })}
              </Button>
            </div>
          </div>
        </Form>
      </Sidebar>
      <TemplateDialog open={templateOpen} onClose={() => setTemplateOpen(false)} />
      <SendDialog
        open={!!sending}
        companyIds={selected}
        reminder={!!sending?.reminder}
        onClose={() => setSending(null)}
        onSent={() => {
          setSending(null);
          setSelected([]);
          reload();
        }}
      />
      <AccountsDialog row={accountsRow} onClose={() => setAccountsOf(null)} onChanged={reload} />
      {totals.CONFIRMED > 0 && (
        <span className="flex items-center gap-1 text-xs opacity-60">
          <CheckCircleOutlined /> {t('campaign.confirmedNote', { count: totals.CONFIRMED })}
        </span>
      )}
    </div>
  );
}
